import { BehaviorSubject, Subject } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DataSource } from '@angular/cdk/collections';
import { DynamicFilterSource } from './dynamic-filter-source';
import { IFlexDataSource } from 'src/app/wephone-utils/datasource/datasource';
import { takeUntil } from 'rxjs/operators';
import { LoggerService } from '@wephone-utils/services';

export class VirtualScrollDataSource extends DataSource<any> {
  private readonly visibleData = new BehaviorSubject<any[]>([]);
  private readonly destroy$ = new Subject<void>();
  private pageSize: number;
  private _data: any[] = [];
  private prevExtraData = 0;
  private startItemIndex = 0;
  private lastStartItemIndex = -1;
  private bottomReached = false;
  private canLoadMoreData = false;
  private resizeObserver: ResizeObserver;
  offsetChange = new BehaviorSubject(0);

  constructor(
    private originalSource: DynamicFilterSource | IFlexDataSource<Object>,
    private viewport?: CdkVirtualScrollViewport,
    private itemHeight: number = 40,
    private isExpansionDetailRow: boolean = false
  ) {
    super();
    this.updatePageSize();
    this.setViewport(viewport);

    this.canLoadMoreData = originalSource && originalSource['load_more'];

    if (originalSource?.dataChanges) {
      originalSource.dataChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
        this._data = val || [];
        if (this.viewport) {
          this.viewport.setTotalContentSize(this.itemHeight * this._data.length);
        }
        this.hasPaginator ? this.visibleData.next(val) : this.sendUpdatedData();
      });
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updatePageSize();
      this.sendUpdatedData();
    });

    this.resizeObserver.observe(document.body);
  }

  get hasPaginator(): boolean {
    return !!this.originalSource.paginator;
  }

  private updatePageSize(): void {
    this.pageSize = Math.ceil(document.body.clientHeight / this.itemHeight) * 2;
  }

  setViewport(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;

    this.viewport.elementScrolled()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.hasPaginator || !this.pageSize) {
          return;
        }

        const scrollTop = this.viewport.measureScrollOffset();
        const startIdx = Math.max(0, Math.floor(scrollTop / this.itemHeight));

        const isScrollingUp = startIdx < this.startItemIndex;
        const isScrollingDown = startIdx > this.startItemIndex;
        if (isScrollingUp || isScrollingDown && !this.bottomReached) {
          this.startItemIndex = startIdx;
          this.prevExtraData = this.startItemIndex > 5 ? 5 : 0;
          this.updateViewport();
        }

        this.checkLoadMore();
      });
  }

  private checkLoadMore(): void {
    if (!this.canLoadMoreData || this.bottomReached || !this._data.length) {
      return;
    }

    const remainingItems = this._data.length - (this.startItemIndex + this.pageSize);
    if (remainingItems < this.pageSize * 0.1) { // When the list is below 10%, load more.
      this.loadMoreData();
    }
  }

  private loadMoreData(): void {
    if (!this.canLoadMoreData) {
      LoggerService.getInstance().warn('Load more data is not available');
      return;
    }

    (this.originalSource as IFlexDataSource<Object>).load_more().then(hasMore => {
      if (hasMore) {
        this.bottomReached = false;
      }
    });
  }

  private updateViewport(): void {
    if (this.lastStartItemIndex === this.startItemIndex) {
      return;
    };

    this.lastStartItemIndex = this.startItemIndex;
    const offset = this.itemHeight * (this.startItemIndex - this.prevExtraData);
    this.viewport.setRenderedContentOffset(offset);

    if (this.offsetChange.getValue() !== offset) {
      this.offsetChange.next(offset);
    }

    requestAnimationFrame(() => this.sendUpdatedData());
  }

  connect(): BehaviorSubject<any[]> {
    return this.visibleData;
  }

  disconnect(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver.disconnect();
  }

  private sendUpdatedData(): void {
    if (!this.pageSize || !this._data) {
      return;
    };

    const cacheSize = this.pageSize;//Math.floor(this.pageSize * 1.5); // Increase cache up to 150%
    const startIndex = Math.max(0, this.startItemIndex - this.prevExtraData);
    const lastIndex = Math.min(this._data.length, startIndex + cacheSize);
    const slicedData = this._data.slice(startIndex, lastIndex);
    this.bottomReached = lastIndex >= this._data.length;

    if (this.bottomReached) {
      this.loadMoreData();
    }

    this.visibleData.next(
      this.isExpansionDetailRow
        ? slicedData.map(el => [el, { detailRow: true, element: el }]).reduce((acc, val) => acc.concat(val), [])
        : slicedData
    );

    // If the user has scrolled to the end, make sure the position doesn't jump
    if (this.viewport && this.bottomReached) {
      setTimeout(() => {
        this.viewport.scrollToOffset(this.viewport.measureScrollOffset() - this.itemHeight);
      }, 10);
    }
  }
}
